
    import {Inject, Options, Vue} from 'vue-property-decorator';
    import {pushNotification} from '@malt/joy-entrypoint/src/js/notification';
    import Autocomplete from '@malt/joy-entrypoint/src/vue/Autocomplete.vue';
    import {DI} from '@/profile-creation-wizard/common/DependencyInjection';
    import LanguageSearcher from '@/profile-creation-wizard/components/for-creation/repository/LanguageSearcher';
    import BottomSheetDisplayer from '@/common/ui/BottomSheetDisplayer';
    import {eventBusProfileCreationWizard} from '@/profile-creation-wizard/common/events';
    import {TipsPart} from '@/profile-creation-wizard/components/for-creation/TipsPart';
    import mutations from '@/profile-creation-wizard/store/creation/mutations';
    import getters from '@/profile-creation-wizard/store/creation/getters';
    import {Getter} from 'vuex-class';
    import {Language, LanguageLevel, ProfileCreationForm} from '@/profile-creation-wizard/types';

    @Options({
        components: {
            Autocomplete,
        },
    })
    export default class LanguageSelection extends Vue {
        @Inject({from: DI.languageSearcher})
        searcher!: LanguageSearcher;
        @Getter(getters.creationForm)
        creationForm!: ProfileCreationForm;

        bottomSheetSelector = '#completion-languages-bottom-sheet';
        dataHeapTooltipID = 'profile-creation-tooltip-languages';
        langs: Language[] = [];

        async created(): Promise<void> {
            await this.fetchLanguages();
            const languagesSaved = this.creationForm.languages;
            if (languagesSaved.length > 0) {
                this.langs.push(...languagesSaved);
            } else {
                this.prefillNativeLanguageAccordingToHost();
            }
        }

        private prefillNativeLanguageAccordingToHost() {
            const languageFound = this.searcher.findByCode(this.$pageContext.language);
            if (!languageFound) {
                return;
            }

            this.langs.push({
                id: 0,
                code: languageFound.code,
                level: LanguageLevel.NATIVE,
                query: languageFound.name,
            });
            this.$store.commit(mutations.changeLanguages, this.keepValidLanguages());
        }

        mounted(): void {
            this.bottomSheetDisplayer = new BottomSheetDisplayer(
                this.bottomSheetSelector,
                this.dataHeapTooltipID,
                this.$el,
                'div.profile-creation-wizard',
            );
        }

        openBottomSheet(): void {
            this.bottomSheetDisplayer.openBottomSheet();
        }

        async fetchLanguages(): Promise<void> {
            await this.searcher.loadLanguages();
        }

        get langsLevel(): {level: LanguageLevel; text: string}[] {
            return [
                {level: LanguageLevel.BASIC, text: this.$msg('profile.new.language.level.BASIC')},
                {level: LanguageLevel.CONVERSATIONAL, text: this.$msg('profile.new.language.level.CONVERSATIONAL')},
                {level: LanguageLevel.FLUENT, text: this.$msg('profile.new.language.level.FLUENT')},
                {level: LanguageLevel.NATIVE, text: this.$msg('profile.new.language.level.NATIVE')},
            ];
        }

        get isNativeLanguageSelected(): boolean {
            return this.langs.length > 0;
        }

        get defaultValue(): string | null {
            return this.langs[0]?.query;
        }

        get additionalLanguages(): Language[] {
            return this.langs.filter((lang) => lang.id > 0);
        }

        noLevelSelected(id: number): boolean {
            return this.langs[id].level === null;
        }

        isLevelSelected(id: number, level: LanguageLevel): boolean {
            return this.langs[id].level === level;
        }

        emitFocus(): void {
            eventBusProfileCreationWizard.emit('creation:focus-changed', TipsPart.languages);
        }

        selectLang(id: number, languageName: string | null): void {
            this.reset(id);

            if (languageName && languageName.length > 0) {
                if (this.hasAlreadyBeenSelected(languageName)) {
                    pushNotification(this.$msg('profile.creation.completion.wizard.languages.notification.alreadySelected'), {level: 'error'});
                    return;
                }

                this.langs[id].query = languageName;
                this.langs[id].code = this.searcher.getCodeByName(languageName);
            }

            this.$store.commit(mutations.changeLanguages, this.keepValidLanguages());
        }

        hasAlreadyBeenSelected(item: string): boolean {
            return this.langs.some((lang) => lang.query === item);
        }

        reset(id: number): void {
            if (this.langs.length > 0) {
                this.langs[id].code = '';
                this.langs[id].query = null;
            }
        }

        selectLevel(id: number, e: any): void {
            this.langs[id].level = e.target.value;
            this.$store.commit(mutations.changeLanguages, this.keepValidLanguages());
        }

        isRemovableLanguage(lang: Language): boolean {
            return lang.id > 0;
        }

        showAdditionalLanguage(): void {
            const maxId = Math.max(...this.langs.map((lang) => lang.id), 0);
            if (!this.isValid(maxId)) {
                pushNotification(this.$msg('profile.creation.completion.wizard.languages.notification.needCompletion'), {level: 'error'});
                return;
            }
            this.langs.push({code: '', level: null, id: maxId + 1, query: null});
        }

        isValid(index: number): boolean {
            const lang = this.langs[index];
            return this.isLanguageComplete(lang);
        }

        removeAdditionalLanguage(id: number): void {
            const toRemoveElement = this.langs.find((lang) => lang.id === id);
            if (!toRemoveElement) {
                return;
            }

            const index = this.langs.indexOf(toRemoveElement);
            this.langs.splice(index, 1);
            for (let index = 0; index < this.langs.length; index++) {
                this.langs[index].id = index;
            }
            this.$store.commit(mutations.changeLanguages, this.keepValidLanguages());
        }

        keepValidLanguages(): Language[] {
            return this.langs.filter(this.isLanguageComplete);
        }

        isLanguageComplete(language: Language): boolean {
            return language.code.trim().length > 0 && language.level !== null;
        }
    }
